import { useEffect, useState } from 'react';
import type { CelebrationTemplateData } from '@om/shared-types';
import { getCelebrationTemplates } from '@/lib/api/celebrationTemplate.service';

const CACHE_TTL_MS = 5 * 60 * 1000;

let cache: { promise: Promise<CelebrationTemplateData[]>; expiresAt: number } | null = null;

const loadTemplates = (): Promise<CelebrationTemplateData[]> => {
  if (cache && Date.now() < cache.expiresAt) return cache.promise;

  const promise = getCelebrationTemplates().catch((error: unknown) => {
    cache = null;
    throw error;
  });
  cache = { promise, expiresAt: Date.now() + CACHE_TTL_MS };
  return promise;
};

export const resetCelebrationTemplatesCache = (): void => {
  cache = null;
};

/** DB-backed celebration templates. A failed or empty load resolves to [] so flows proceed without a template. */
export function useCelebrationTemplates(): { templates: CelebrationTemplateData[]; loading: boolean } {
  const [templates, setTemplates] = useState<CelebrationTemplateData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadTemplates()
      .then((result) => {
        if (!cancelled) setTemplates(result);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { templates, loading };
}
